import { CheckIcon, LockSimpleIcon } from '@phosphor-icons/react';
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TOTAL_STEPS,
  trackerSteps,
  type OrderStage,
  type StepState,
  type TrackerStepState,
} from '@entities/search-session';

import styles from './StepTracker.module.css';

interface StepTrackerProps {
  stage: OrderStage;
  paid: boolean;
  /**
   * Переход на экран пройденного шага. Не передан — трекер целиком
   * неинтерактивен (гость на публичном экране, архивный заказ).
   */
  onNavigate?: ((step: TrackerStepState) => void) | undefined;
  /**
   * Есть ли у пройденного шага экран, на который можно уйти. Не передан —
   * экран есть у каждого пройденного шага.
   *
   * Нужен там, где шаг пройден, а вести некуда: у оплаченного заказа шаг 3
   * «Zahlung» пройден, но экрана квитанции нет, а экран оплаты сам уводит
   * оплаченный заказ на четвёрку. Такой шаг рисуется **прежним статичным
   * видом** — с галочкой и состоянием, но не кнопкой: кнопка, которая никуда
   * не ведёт или уводит не туда, хуже её отсутствия.
   */
  canNavigate?: ((step: TrackerStepState) => boolean) | undefined;
}

/**
 * Ступени, которыми трекер уступает место, если строка шапки не вмещает то,
 * что назначил медиазапрос: сначала укорачиваются разделители, потом остаётся
 * подпись только текущего шага, потом круги без подписей становятся теснее, и
 * лишь в последнюю очередь пропадает подпись текущего шага — ради неё трекер и
 * стоит в шапке. Номер, галочка и замок видны всегда, а полное состояние
 * каждого шага скринридер слышит на любой ступени.
 */
const SQUEEZE_LEVELS = ['none', 'narrow', 'current', 'tight', 'markers'] as const;
type SqueezeLevel = (typeof SQUEEZE_LEVELS)[number];

/**
 * Страховка от наезда трекера на соседей по шапке.
 *
 * Сколько подписей показывать, решает медиазапрос (`StepTracker.module.css`),
 * но ширина строки зависит не только от окна: от языка («Verhandlung» против
 * «Negotiation»), от того, какой шаг текущий (его подпись видна всегда и
 * набрана жирнее), и от длины адреса в меню аккаунта рядом. Медиазапрос этого
 * не знает, поэтому трекер меряет себя сам: если строка не помещается в
 * отведённое ей место, он спускается на ступень ниже (`SQUEEZE_LEVELS`) — до
 * тех пор, пока не поместится. Подпись не обрезается посередине слова никогда:
 * она либо видна целиком, либо не показывается.
 *
 * Перемер идёт при каждой смене ширины, языка, стадии и после загрузки
 * шрифтов: до неё строка набрана запасным шрифтом другой ширины. Замер и
 * поправка выполняются до отрисовки, поэтому наезда не видно и на миг.
 */
function useSqueeze(contentKey: string): {
  rootRef: RefObject<HTMLDivElement | null>;
  listRef: RefObject<HTMLOListElement | null>;
  squeeze: SqueezeLevel;
} {
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const [width, setWidth] = useState(0);
  const [fontsEpoch, setFontsEpoch] = useState(0);
  const [applied, setApplied] = useState({ key: '', level: 0 });

  // Новая ширина или новое содержимое — замер заново с полной ступени.
  const key = `${width}|${fontsEpoch}|${contentKey}`;
  const level = applied.key === key ? applied.level : 0;

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!('fonts' in document)) return;
    let active = true;
    const remeasure = () => {
      if (active) setFontsEpoch((value) => value + 1);
    };
    void document.fonts.ready.then(remeasure);
    document.fonts.addEventListener('loadingdone', remeasure);
    return () => {
      active = false;
      document.fonts.removeEventListener('loadingdone', remeasure);
    };
  }, []);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // Список не шире отведённого места, а шаги не сжимаются: то, что не
    // поместилось, вылезает за край списка. Пиксель допуска — округление
    // дробных ширин, а не наезд: до соседей по шапке ещё 20 px.
    // Скрытый список (компактная строка на узком экране) весит ноль и
    // помещается всегда — уступать там нечего.
    const overflows = list.scrollWidth - list.clientWidth > 1;
    if (overflows && level < SQUEEZE_LEVELS.length - 1) {
      setApplied({ key, level: level + 1 });
    }
  }, [key, level]);

  return { rootRef, listRef, squeeze: SQUEEZE_LEVELS[level] ?? 'none' };
}

/**
 * Трекер семи шагов (FE-192, MVP_PRD §33, DESIGN_SYSTEM §5.3).
 *
 * Это **индикатор, а не меню**: клик по будущему шагу невозможен, клик по
 * пройденному ведёт на его экран. Шаг, закрытый оплатой, показывается с замком
 * и текстом причины — не скрывается: ложных обещаний нет, но и умалчивания о
 * том, что будет дальше, тоже.
 *
 * Разметка — `<ol>` с `aria-current="step"`; каждый шаг несёт полное текстовое
 * состояние для скринридера («Schritt 3 von 7, Zahlung, abgeschlossen»).
 */
export function StepTracker({ stage, paid, onNavigate, canNavigate }: StepTrackerProps) {
  const { t, i18n } = useTranslation();
  const steps = trackerSteps({ stage, paid });
  const current = steps.find((step) => step.state === 'current');
  const { rootRef, listRef, squeeze } = useSqueeze(
    `${i18n.resolvedLanguage ?? i18n.language}|${stage}|${String(paid)}`,
  );

  const stateLabel = (state: StepState) => t(`app.tracker.state.${state}`);

  const describe = (step: TrackerStepState) =>
    t('app.tracker.stepStatus', {
      number: step.number,
      total: TOTAL_STEPS,
      name: t(`app.tracker.step.${step.key}`),
      state: stateLabel(step.state),
    });

  return (
    <div ref={rootRef} className={styles.root}>
      <ol
        ref={listRef}
        className={styles.tracker}
        aria-label={t('app.tracker.label')}
        data-squeeze={squeeze}
      >
        {steps.map((step) => {
          const label = t(`app.tracker.step.${step.key}`);
          const interactive =
            step.navigable && onNavigate !== undefined && (canNavigate?.(step) ?? true);

          const marker = (
            <>
              <span className={styles.circle} aria-hidden="true">
                {step.state === 'done' ? (
                  <CheckIcon size={14} weight="bold" />
                ) : step.state === 'locked' ? (
                  <LockSimpleIcon size={13} weight="fill" />
                ) : (
                  <span className="tabular">{step.number}</span>
                )}
              </span>
              <span className={styles.name} aria-hidden="true">
                {label}
              </span>
              {/* Полное состояние словами: скринридер не видит ни цвета круга,
                  ни иконки замка. */}
              <span className="visually-hidden">{describe(step)}</span>
            </>
          );

          return (
            <li
              key={step.key}
              className={[styles.step, styles[step.state]].filter(Boolean).join(' ')}
              aria-current={step.state === 'current' ? 'step' : undefined}
            >
              {interactive ? (
                <button
                  type="button"
                  className={styles.trigger}
                  onClick={() => {
                    onNavigate(step);
                  }}
                >
                  {marker}
                </button>
              ) : (
                <span
                  className={styles.trigger}
                  // Замок объясняет причину и мышью, и с клавиатуры: «недоступно»
                  // без причины — дефект (DESIGN_SYSTEM §5.9).
                  title={step.state === 'locked' ? t('app.tracker.lockedReason') : undefined}
                >
                  {marker}
                </span>
              )}
            </li>
          );
        })}
      </ol>

      {/* Мобильный вариант: «Schritt 4 von 7 · Bewertung» и полоса прогресса.
          Семь подписей в 370 px не помещаются, а горизонтальный скролл шапки —
          критический дефект (DESIGN_SYSTEM §4.4). */}
      <div className={styles.compact}>
        {current ? (
          <p className={styles.compactLabel}>
            {t('app.tracker.compact', {
              number: current.number,
              total: TOTAL_STEPS,
              name: t(`app.tracker.step.${current.key}`),
            })}
          </p>
        ) : (
          <p className={styles.compactLabel}>{t('app.tracker.compactInactive')}</p>
        )}
        <span className={styles.compactTrack} aria-hidden="true">
          <span
            className={styles.compactFill}
            style={{ transform: `scaleX(${(current?.number ?? 0) / TOTAL_STEPS})` }}
          />
        </span>
      </div>
    </div>
  );
}
